import _ from 'lodash';

const ADD_PREFIX = 'ADD_';
const ADD_PREFIX_LENGTH = ADD_PREFIX.length;

// Arithmetic operators read as their math signs (matching the builder's operator
// chips) — "100 + (50 × 10)" flows better in a formula readback than words. Note:
// function-phrase "plus" (e.g. "plus 5 business days") and comparison words stay
// as words; only bare arithmetic operators use signs.
const ARITHMETIC_GLOSSES = {
  '+': '+',
  '-': '-',
  '*': '×',
  '/': '÷',
};

const FRIENDLY_TEMPLATES = {
  PERCENT: args => (args.length >= 2 ? `${args[0]}% of ${args[1]}` : null),
  CONCAT: args => args.join(', then '),
  TO_STRING: args => `text of ${args[0] ?? ''}`.trim(),
  EMPTY: args => `${args[0] ?? ''} is empty`.trim(),
  FORMAT_DATE: args => `formatted date of ${args[0] ?? ''}`.trim(),
  CONVERT_DATE: args =>
    args[1] ? `${args[0] ?? ''} in ${args[1]}`.trim() : `${args[0] ?? ''}`.trim(),
  GET_DATE: args => `date part of ${args[0] ?? ''}`.trim(),
  GET_YEAR: args => `year of ${args[0] ?? ''}`.trim(),
  DATE_NOW: () => 'now',
  ADD_BUSINESS_DAYS: args => (args.length >= 2 ? `${args[0]} plus ${args[1]} business days` : null),
};

const renderAddOffset = (upper, args) => {
  if (!upper.startsWith(ADD_PREFIX) || args.length < 2) {
    return null;
  }

  const unit = upper.slice(ADD_PREFIX_LENGTH).toLowerCase();

  return `${args[0]} plus ${args[1]} ${unit}`;
};

/**
 * Render a function call to friendly English using already-rendered argument
 * strings. Returns null when there is no friendly template for the function,
 * signalling the caller to fall back to its own generic rendering.
 *
 * @param {{ fnName: string, argTexts: string[] }} params
 * @returns {string|null}
 */
const renderFriendlyFunction = ({ fnName, argTexts = [] }) => {
  if (_.isNil(fnName)) {
    return null;
  }

  const upper = String(fnName).toUpperCase();
  const template = FRIENDLY_TEMPLATES[upper];

  if (template) {
    return template(argTexts);
  }

  return renderAddOffset(upper, argTexts);
};

/**
 * Render an N-ary arithmetic operation to friendly English using
 * already-rendered operand strings. Falls back to the raw operator when the
 * operator is not a recognised arithmetic operator. Callers may pass an
 * `operandTexts` array (preferred, supports 3+ operands) or the legacy
 * `leftText`/`rightText` pair (binary).
 *
 * @param {{ op: string, operandTexts?: string[], leftText?: string, rightText?: string }} params
 * @returns {string}
 */
const renderFriendlyArithmetic = ({ op, operandTexts, leftText, rightText }) => {
  const gloss = ARITHMETIC_GLOSSES[op] ?? op;
  const parts = operandTexts ?? [leftText, rightText];

  return parts.filter(part => !_.isNil(part)).join(` ${gloss} `);
};

export { renderFriendlyFunction, renderFriendlyArithmetic };
